/**
 * AerationKnowledgeBasePage.tsx — Cross-site synthesis of documented
 *                                  aeration interventions, with the parameters
 *                                  that distinguish a good bed and a good
 *                                  aeration scheme.
 */

import { useNavigate } from 'react-router';
import {
  Lightbulb, ArrowLeft, Layers, Wind, ExternalLink, CheckCircle2,
  XCircle, AlertTriangle, BookOpen, FileText, Wrench, Workflow,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  AERATION_CASES, SUCCESS_FACTORS, GOOD_BED_INDICATORS,
  GOOD_AERATION_INDICATORS, DESIGN_TEMPLATES,
} from '@/lib/aerationKnowledge';
import { MODULES } from '@/lib/moduleRegistry';
import ModuleInfoModal from '@/components/ModuleInfoModal';

export default function AerationKnowledgeBasePage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Lightbulb} title="Aeration Knowledge Base — Cross-Site Synthesis"
        subtitle="What works, what doesn't — distilled from documented aeration trials world-wide, mapped to the modules that predict each effect"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">How this synthesis was built:</strong> seven documented aeration interventions
        (Bautzen, Sempach, Hallwil, Hanningfield, Rutland, Amisk, Black Lake) cover the range of physical regimes
        (shallow polymictic → deep stratified), system types (air destratifier, LOX hypolimnetic, Speece-cone, Helixor),
        and outcome metrics (DO, P, Mn, cyano dominance, geosmin). Cross-referencing the design parameters with the
        documented outcomes yields a set of <em>common success factors</em> that any new scheme should satisfy.
      </div>

      {/* ──────────── Section 1: Cases ──────────── */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-sky-300" />
          1. Documented aeration interventions
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b-2 border-slate-700 text-slate-400">
                <th className="py-2 pr-3">Site</th>
                <th className="py-2 pr-3">System</th>
                <th className="py-2 pr-3">Diffuser</th>
                <th className="py-2 pr-3">Mean / max depth (m)</th>
                <th className="py-2 pr-3">Q_air / diff (L/s)</th>
                <th className="py-2 pr-3">Diff depth (m)</th>
                <th className="py-2 pr-3">Observed OTE</th>
                <th className="py-2 pr-3">Key outcome</th>
              </tr>
            </thead>
            <tbody>
              {AERATION_CASES.map(c => (
                <tr key={c.id} className="border-b border-slate-800/80 align-top">
                  <td className="py-2 pr-3">
                    <div className="font-semibold text-slate-100">{c.site}</div>
                    <div className="text-[10px] text-slate-500">{c.country} · {c.installDate}</div>
                  </td>
                  <td className="py-2 pr-3 text-slate-300">{c.systemType}</td>
                  <td className="py-2 pr-3 text-slate-300">{c.diffuserType}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">{c.basinMeanDepth_m} / {c.basinMaxDepth_m}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">{c.Q_air_perDiffuser_Ls > 0 ? c.Q_air_perDiffuser_Ls : 'LOX'}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">{c.diffuserDepth_m}</td>
                  <td className="py-2 pr-3 font-mono text-emerald-300">{c.observedOTE_pct} %</td>
                  <td className="py-2 pr-3 text-slate-300 max-w-xs">
                    <details className="cursor-pointer">
                      <summary className="text-sky-300 hover:text-sky-200">Outcomes & refs ↓</summary>
                      <ul className="list-disc ml-4 mt-1 space-y-0.5 text-[11px]">
                        {c.observedOutcomes.map((o, i) => <li key={i}>{o}</li>)}
                      </ul>
                      <div className="mt-2 text-[11px] text-emerald-300">
                        <strong>✓ What worked:</strong> {c.whatWorked}
                      </div>
                      <div className="mt-1 text-[11px] text-amber-300">
                        <strong>⚠ What failed:</strong> {c.whatFailed}
                      </div>
                      <div className="mt-2 space-y-0.5">
                        {c.refs.map((r, i) => (
                          <div key={i} className="text-[10px] text-slate-500 italic">
                            {r.citation}
                            {r.doi && (
                              <a href={`https://doi.org/${r.doi}`} target="_blank" rel="noopener noreferrer"
                                className="ml-1 text-sky-400 hover:text-sky-300 inline-flex items-center gap-0.5 not-italic">
                                doi:{r.doi} <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ──────────── Section 2: Success factors ──────────── */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-300" />
          2. Common success factors across all sites
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b-2 border-slate-700 text-slate-400">
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Parameter</th>
                <th className="py-2 pr-3">Target range</th>
                <th className="py-2 pr-3">Why</th>
                <th className="py-2 pr-3">Predicted by</th>
                <th className="py-2 pr-3">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {SUCCESS_FACTORS.map((f, i) => {
                const mod = MODULES.find(m => m.id === f.predictedBy);
                return (
                  <tr key={i} className="border-b border-slate-800/80 align-top">
                    <td className="py-1.5 pr-3"><CatTag cat={f.category} /></td>
                    <td className="py-1.5 pr-3 text-slate-200 font-semibold">{f.parameter}</td>
                    <td className="py-1.5 pr-3 text-emerald-300 font-mono">{f.targetRange}</td>
                    <td className="py-1.5 pr-3 text-slate-400 max-w-md">{f.rationale}</td>
                    <td className="py-1.5 pr-3">
                      {mod ? <ModuleInfoModal moduleId={f.predictedBy} triggerLabel={mod.label} /> : <code>{f.predictedBy}</code>}
                    </td>
                    <td className="py-1.5 pr-3 text-[10px] text-slate-500">{f.evidenceFrom.join(', ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ──────────── Section 3: Good bed ──────────── */}
      <section className="rounded-2xl border border-emerald-700/40 bg-emerald-950/20 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Layers className="w-4 h-4 text-emerald-300" />
          3. What makes a "good bed" — sediment-side criteria
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GOOD_BED_INDICATORS.map((b, i) => (
            <IndicatorCard key={i} ind={b} />
          ))}
        </div>
      </section>

      {/* ──────────── Section 4: Good aeration ──────────── */}
      <section className="rounded-2xl border border-sky-700/40 bg-sky-950/20 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Wind className="w-4 h-4 text-sky-300" />
          4. What makes a "good aeration" — hardware-side criteria
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GOOD_AERATION_INDICATORS.map((a, i) => (
            <IndicatorCard key={i} ind={a} />
          ))}
        </div>
      </section>

      {/* ──────────── Section 5: Design templates ──────────── */}
      <section className="rounded-2xl border border-fuchsia-700/40 bg-fuchsia-950/20 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-fuchsia-300" />
          5. Synthesised design templates
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b-2 border-slate-700 text-slate-400">
                <th className="py-2 pr-3">Scenario</th>
                <th className="py-2 pr-3">Diffuser type</th>
                <th className="py-2 pr-3">Pore (µm)</th>
                <th className="py-2 pr-3">Bubble d (mm)</th>
                <th className="py-2 pr-3">Q_air (L/s)</th>
                <th className="py-2 pr-3">Spacing</th>
                <th className="py-2 pr-3">Layout</th>
                <th className="py-2 pr-3">Above bed</th>
                <th className="py-2 pr-3">Target OTE</th>
                <th className="py-2 pr-3">Margin / SOD</th>
              </tr>
            </thead>
            <tbody>
              {DESIGN_TEMPLATES.map((d, i) => (
                <tr key={i} className="border-b border-slate-800/80 align-top">
                  <td className="py-2 pr-3 text-slate-100 font-semibold max-w-xs">{d.scenario}</td>
                  <td className="py-2 pr-3 text-slate-300">{d.diffuserType}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">{d.poreSize_um || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">{d.bubbleSize_mm}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">{d.Q_air_perDiffuser_Ls || 'LOX'}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">× {d.spacing_x_plumeDiameter} plume Ø</td>
                  <td className="py-2 pr-3 text-slate-300 max-w-xs">{d.layout}</td>
                  <td className="py-2 pr-3 font-mono text-slate-300">{d.depthAboveBed_m} m</td>
                  <td className="py-2 pr-3 font-mono text-emerald-300">{d.targetOTE_pct} %</td>
                  <td className="py-2 pr-3 font-mono text-emerald-300">× {d.targetMargin_overSOD}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ──────────── Footer: jump-off links ──────────── */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400">
        <div className="font-semibold text-slate-200 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4 text-rb-brand" /> Apply this knowledge
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate('/module-trains')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white">
            <Workflow className="w-3.5 h-3.5" /> Module Trains Guide
          </button>
          <button onClick={() => navigate('/benchmark-sites')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-700 bg-sky-900/30 px-3 py-1.5 text-xs font-semibold text-sky-200 hover:bg-sky-900/50">
            Benchmark sites & bathymetry
          </button>
          <button onClick={() => navigate('/modules')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700">
            All modules
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function CatTag({ cat }: { cat: string }) {
  const colour = cat === 'Plume' ? 'bg-sky-900/50 text-sky-200 border-sky-700' :
                 cat === 'Bed' ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700' :
                 cat === 'Hypolimnion O₂' ? 'bg-blue-900/40 text-blue-200 border-blue-700' :
                 cat === 'Placement' ? 'bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700' :
                 cat === 'Algae' ? 'bg-amber-900/40 text-amber-200 border-amber-700' :
                                   'bg-red-900/40 text-red-200 border-red-700';
  return <span className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${colour}`}>{cat}</span>;
}

function IndicatorCard({ ind }: { ind: { parameter: string; unit: string; goodRange: string; poorRange: string; why: string; module: string; } }) {
  const mod = MODULES.find(m => m.id === ind.module);
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3">
      <div className="flex justify-between items-baseline">
        <div className="text-sm font-semibold text-slate-100">{ind.parameter}</div>
        <div className="text-[10px] text-slate-500 font-mono">{ind.unit}</div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md border border-emerald-700/50 bg-emerald-900/15 p-2">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Good</div>
          <div className="text-emerald-100 font-mono mt-0.5">{ind.goodRange}</div>
        </div>
        <div className="rounded-md border border-red-700/50 bg-red-900/15 p-2">
          <div className="text-[10px] uppercase tracking-wider text-red-300 flex items-center gap-1"><XCircle className="w-3 h-3" /> Poor</div>
          <div className="text-red-100 font-mono mt-0.5">{ind.poorRange}</div>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-slate-400 leading-snug flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
        <span>{ind.why}</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <span className="text-[10px] text-slate-500">Module:</span>
        {mod ? <ModuleInfoModal moduleId={ind.module} triggerLabel={mod.label} /> : <code className="text-[10px] text-slate-500">{ind.module}</code>}
      </div>
    </div>
  );
}
